import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Gauge, ChartPieSlice, Package, Wrench, Flask, LockKey, SignOut,
} from '@phosphor-icons/react';
import { InstrDashboardTab } from '../components/instrumentation/InstrDashboardTab';
import { InstrMaintenanceTab } from '../components/instrumentation/InstrMaintenanceTab';
import { InstrQualityTab } from '../components/instrumentation/InstrQualityTab';
import { InstrStockTab } from '../components/instrumentation/InstrStockTab';

const TABS = [
  { v: 'dashboard',   l: 'Dashboard',       Ic: ChartPieSlice, C: InstrDashboardTab },
  { v: 'maintenance', l: 'Maintenance',     Ic: Wrench,        C: InstrMaintenanceTab },
  { v: 'quality',     l: 'Quality Control', Ic: Flask,         C: InstrQualityTab },
  { v: 'stock',       l: 'Stock',           Ic: Package,       C: InstrStockTab },
];

export default function InstrumentationOpsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FCFAF6]" data-testid="instrumentation-ops-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0A2540] text-white border-b border-[#0A2540]/30">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 rounded-xl bg-[#C9A35B]/20 text-[#C9A35B] flex items-center justify-center shrink-0">
              <Gauge size={18} weight="duotone" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Instrumentation Operations
              </p>
              <p className="text-sm font-semibold truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {user.fullname || user.username}
              </p>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center font-semibold text-xs"
                data-testid="io-avatar"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                {(user.fullname || user.username || 'U').slice(0, 1).toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuLabel>
                <p className="text-xs font-semibold text-[#1A1D1C] truncate">{user.fullname || user.username}</p>
                <p className="text-[10px] text-[#78716C] truncate">{user.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate('/change-password')}
                data-testid="io-change-password"
                className="cursor-pointer text-[#0A2540] focus:bg-[#0A2540]/5"
              >
                <LockKey size={14} className="mr-2" /> Change password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={logout}
                data-testid="io-logout"
                className="text-[#EF4444] focus:text-[#EF4444] focus:bg-[#EF4444]/10 cursor-pointer"
              >
                <SignOut size={14} className="mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList className="bg-white border border-[#EDE6D8] rounded-xl p-1 h-auto flex flex-wrap">
            {TABS.map(({ v, l, Ic }) => (
              <TabsTrigger
                key={v}
                value={v}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white"
                data-testid={`io-tab-${v}`}
              >
                <Ic size={14} weight="duotone" /> {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="rounded-2xl border border-[#EDE6D8] bg-white/40 p-3 sm:p-4 min-h-[400px]">
            {TABS.map(({ v, C }) => (
              <TabsContent key={v} value={v} className="mt-0">
                <C />
              </TabsContent>
            ))}
          </div>
        </Tabs>
      </main>
    </div>
  );
}
